<template>
  <div class="statuses">

    <header class="masthead">
      <h1 class="title">Statuses<span class="dot">.</span></h1>
      <p class="sub">
        What people can set on themselves under Change Status. The icon is what
        the display shows in place of their office badge.
      </p>
    </header>

    <!-- Add -->
    <div class="form">
      <label class="field grow">
        <span class="field-label">Name</span>
        <input v-model="form.label" class="input" type="text" placeholder="Parental leave">
      </label>

      <div class="field">
        <span class="field-label">Icon</span>
        <div class="icons">
          <button
            v-for="icon in ICONS"
            :key="icon"
            class="icon"
            :class="{ on: form.icon === icon }"
            type="button"
            :aria-label="icon"
            @click="form.icon = icon"
          >{{ icon }}</button>
        </div>
      </div>

      <button class="btn primary" :disabled="!canAdd || adding" @click="add">
        {{ adding ? 'Adding…' : 'Add status' }}
      </button>
    </div>

    <p v-if="formError" class="msg err">{{ formError }}</p>

    <!-- List -->
    <p v-if="loading" class="msg">Reading statuses</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="types.length === 0" class="msg">Nothing set up yet</p>

    <table v-else class="table">
      <thead>
        <tr>
          <th class="col-order"></th>
          <th class="col-icon">Icon</th>
          <th class="col-name">Name</th>
          <th class="col-hours">Asks for hours</th>
          <th class="col-offered">Offered</th>
          <th class="col-actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(t, i) in types" :key="t.value" :class="{ off: !t.active }">
          <td class="order">
            <button class="link" :disabled="i === 0" @click="move(i, i - 1)">Up</button>
            <button class="link" :disabled="i === types.length - 1" @click="move(i, i + 1)">Down</button>
          </td>

          <td>
            <div class="icons compact">
              <button
                v-for="icon in ICONS"
                :key="icon"
                class="icon"
                :class="{ on: t.icon === icon }"
                type="button"
                :aria-label="icon"
                @click="setIcon(t, icon)"
              >{{ icon }}</button>
            </div>
          </td>

          <td>
            <input
              class="name-input"
              type="text"
              :value="draft[t.value] ?? t.label"
              @input="draft[t.value] = $event.target.value"
              @blur="commitLabel(t)"
              @keyup.enter="$event.target.blur()"
            >
            <!-- The code is what every entry already recorded points at, so it
                 is fixed once created. Shown because it explains why renaming is
                 safe. -->
            <span class="code">{{ t.value }}</span>
          </td>

          <td>
            <!-- Whether setting this status asks the person for working hours.
                 True for working from home, false for leave: Vero cannot
                 observe a home office, so the hours have to be given, and there
                 are none to give for a day off. -->
            <label class="toggle">
              <input type="checkbox" :checked="t.asksForHours" @change="setHours(t, $event.target.checked)">
              <span class="track"><span class="knob"></span></span>
            </label>
          </td>

          <td>
            <label class="toggle">
              <input type="checkbox" :checked="t.active" @change="setActive(t, $event.target.checked)">
              <span class="track"><span class="knob"></span></span>
            </label>
          </td>

          <td class="actions">
            <span v-if="state[t.value]" class="state">{{ state[t.value] }}</span>
            <button class="link danger" @click="remove(t)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="!loading && !error" class="foot">
      A status somebody has used cannot be deleted, because the entries that use
      it would stop making sense. Switch it off instead: it disappears from the
      dropdown and the history stays readable.
    </p>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { getStatusTypes, setStatusType, deleteStatusType } from '@/services/StatusService'

// A fixed palette rather than free text.
//
// The display renders these at badge size on a wall, where an arbitrary emoji
// may be unrecognisable or render differently on the screen's font. These are
// chosen to read at a distance and to look like what they mean.
const ICONS = [
  '\u{1F334}', // palm tree
  '\u{2708}\u{FE0F}', // aeroplane
  '\u{1F3E0}', // house
  '\u{1F393}', // graduation cap
  '\u{1F4C5}', // calendar
  '\u{1F912}', // thermometer face
  '\u{1F476}', // baby
  '\u{1F3E5}', // hospital
  '\u{1F4BB}', // laptop
  '\u{1F697}', // car
  '\u{2615}', // hot drink
  '\u{1F4DA}'  // books
]

const types = ref([])
const draft = ref({})
const state = ref({})

const loading = ref(true)
const error = ref(null)
const formError = ref(null)
const adding = ref(false)

const form = ref({ label: '', icon: ICONS[0] })

const canAdd = computed(() => form.value.label.trim().length > 0)

function flash(code, value) {
  state.value = { ...state.value, [code]: value }
  setTimeout(() => {
    const next = { ...state.value }
    if (next[code] === value) delete next[code]
    state.value = next
  }, 1800)
}

async function load() {
  loading.value = true
  error.value = null

  try {
    // Everything, including the switched-off ones: this is the page where they
    // are switched back on.
    types.value = await getStatusTypes({ activeOnly: false })
    draft.value = {}
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function save(type) {
  await setStatusType(type)
}

async function add() {
  formError.value = null

  const label = form.value.label.trim()

  if (types.value.some((t) => t.label.toLowerCase() === label.toLowerCase())) {
    formError.value = 'A status with that name already exists'
    return
  }

  adding.value = true

  try {
    // No code: the server derives one from the label and returns it.
    await save({ label, icon: form.value.icon, position: types.value.length, active: true, asksForHours: false })
    form.value = { label: '', icon: ICONS[0] }
    await load()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  } finally {
    adding.value = false
  }
}

async function commitLabel(type) {
  const typed = (draft.value[type.value] ?? type.label).trim()
  if (typed === type.label || typed === '') {
    draft.value = { ...draft.value, [type.value]: type.label }
    return
  }

  try {
    await save({ ...type, label: typed })
    type.label = typed
    flash(type.value, 'saved')
  } catch (err) {
    console.error(err)
    flash(type.value, err.message ?? 'failed')
    draft.value = { ...draft.value, [type.value]: type.label }
  }
}

async function setIcon(type, icon) {
  const previous = type.icon
  type.icon = icon

  try {
    await save({ ...type, icon })
    flash(type.value, 'saved')
  } catch (err) {
    console.error(err)
    type.icon = previous
    flash(type.value, err.message ?? 'failed')
  }
}

async function setHours(type, asksForHours) {
  const previous = type.asksForHours
  type.asksForHours = asksForHours

  try {
    await save({ ...type, asksForHours })
    flash(type.value, 'saved')
  } catch (err) {
    console.error(err)
    type.asksForHours = previous
    flash(type.value, err.message ?? 'failed')
  }
}

async function setActive(type, active) {
  const previous = type.active
  type.active = active

  try {
    await save({ ...type, active })
    flash(type.value, active ? 'offered' : 'hidden')
  } catch (err) {
    console.error(err)
    type.active = previous
    flash(type.value, err.message ?? 'failed')
  }
}

async function move(from, to) {
  if (to < 0 || to >= types.value.length) return

  const next = [...types.value]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  types.value = next

  // Positions are rewritten for the whole list rather than swapping two: a move
  // changes the position of everything between, and half-applied ordering is
  // the kind of thing nobody notices until the dropdown looks wrong.
  try {
    for (let i = 0; i < next.length; i++) {
      await save({ ...next[i], position: i })
    }
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
    await load()
  }
}

async function remove(type) {
  const ok = window.confirm(`Delete ${type.label}?`)
  if (!ok) return

  try {
    await deleteStatusType(type.value)
    await load()
  } catch (err) {
    console.error(err)
    // The server refuses when the status is in use, and its message explains
    // what to do instead. Shown against the row rather than at the top, where
    // it would be unclear which status it referred to.
    flash(type.value, err.message ?? 'could not delete')
  }
}

onMounted(load)
</script>


<style scoped>
.statuses {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --on: #0f9d58;

  max-width: 1000px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 24px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 8px 0 0;
  max-width: 62ch;
}

/* ---------- Add form ---------- */

.form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field.grow { flex: 1; min-width: 220px; }

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.input {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: #191c1e;
  outline: none;
  box-sizing: border-box;
  width: 100%;
}

.input:focus { border-color: var(--ink); }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:disabled { opacity: 0.4; cursor: default; }
.btn.primary { background: var(--ink); color: #fff; }
.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- Icons ---------- */

.icons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 300px;
}

.icons.compact { max-width: 220px; }

.icon {
  font-size: 16px;
  line-height: 1;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.icon:hover { background: #f1f4f7; }

.icon.on {
  border-color: var(--ink);
  background: #eaf1f7;
}

/* ---------- Table ---------- */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 0 12px 10px 0;
  border-bottom: 1px solid var(--line);
}

.table td {
  padding: 10px 12px 10px 0;
  border-bottom: 1px solid #f1f4f7;
  vertical-align: middle;
}

.col-order { width: 10%; }
.col-icon { width: 22%; }
.col-name { width: 28%; }
.col-hours { width: 12%; }
.col-offered { width: 10%; }
.col-actions { width: 18%; }

/* A switched-off status is dimmed rather than hidden: this is the page where it
   gets switched back on. */
tr.off .name-input,
tr.off .code { opacity: 0.45; }

.order { white-space: nowrap; }
.order .link { margin-right: 10px; }

.name-input {
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  outline: none;
  box-sizing: border-box;
  color: var(--ink);
}

.name-input:hover { border-color: var(--line); }
.name-input:focus { border-color: var(--ink); background: #fff; }

.code {
  display: block;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11.5px;
  color: #a3adb9;
  padding-left: 10px;
  margin-top: 2px;
}

/* ---------- Toggle ---------- */

.toggle { cursor: pointer; }

.toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.track {
  display: block;
  width: 42px;
  height: 24px;
  border-radius: 12px;
  background: #cbd5dd;
  position: relative;
  transition: background 0.18s ease;
}

.knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.18s ease;
}

.toggle input:checked + .track { background: var(--on); }
.toggle input:checked + .track .knob { transform: translateX(18px); }

/* ---------- Messages ---------- */

.actions { text-align: right; white-space: nowrap; }

.state {
  font-size: 12.5px;
  color: var(--ink-soft);
  margin-right: 12px;
}

.foot {
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 20px 0 0;
  max-width: 62ch;
}

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
  max-width: 60ch;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.link:disabled { opacity: 0.3; cursor: default; text-decoration: none; }
.link.danger { color: #b3261e; }

@media (max-width: 800px) {
  .statuses { padding: 0 20px 60px; }
  .form { flex-direction: column; align-items: stretch; }
  .icons { max-width: none; }
}

@media (prefers-reduced-motion: reduce) {
  .track, .knob { transition: none; }
}
</style>
